'use client';
import React from 'react';

const features = [
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        <path d="M2 12h20" />
      </svg>
    ),
    title: 'Nearby Airport Savings',
    desc: 'If flying from a neighboring airport drops your airfare, our intelligent scanner alerts you instantly to maximize your savings.',
    bg: 'bg-blue-50 text-blue-600 border-blue-100/50',
    glow: 'hover:shadow-[0_8px_30px_rgba(37,99,235,0.06)] hover:border-blue-300',
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3.5c-.5-.5-2.5 0-4 1.5L13.5 8.5l-8.2-1.8-1.8 1.8 7 4.2-3.5 3.5-3-1-1.5 1.5 4.5 2.5 2.5 4.5 1.5-1.5-1-3 3.5-3.5 4.2 7 1.8-1.8z" />
      </svg>
    ),
    title: 'Deals from 500+ Airlines',
    desc: 'Access exclusive, unpublished bargain rates negotiated directly with over 500 domestic and international carriers worldwide.',
    bg: 'bg-accent/10 text-accent border-accent/20',
    glow: 'hover:shadow-[0_8px_30px_rgba(0,98,227,0.06)] hover:border-accent/40',
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="m9 11 2 2 4-4" />
      </svg>
    ),
    title: 'Safe & Secure Bookings',
    desc: 'Shop first-class security. Every transaction is safeguarded by bank-grade SSL encryption and full PCI-DSS compliance protocols.',
    bg: 'bg-emerald-50 text-emerald-600 border-emerald-100/50',
    glow: 'hover:shadow-[0_8px_30px_rgba(16,185,129,0.06)] hover:border-emerald-300',
  },
  {
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M8 10h.01" />
        <path d="M12 10h.01" />
        <path d="M16 10h.01" />
      </svg>
    ),
    title: '24/7 Customer Support',
    desc: 'Speak directly with expert booking specialists. Our elite support desk is actively on call round-the-clock for flight assistance.',
    bg: 'bg-sky-50 text-sky-600 border-sky-100/50',
    glow: 'hover:shadow-[0_8px_30px_rgba(56,189,248,0.06)] hover:border-sky-300',
  },
];

export default function FeaturesSection() {
  return (
    <div className="py-20 bg-slate-50 border-y border-slate-100">
      <div className="max-w-[1170px] mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div
            style={{ height: '30px' }}
            className="inline-flex items-center gap-1.5 bg-accent/15 border border-accent/30 px-3.5 py-1.2 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 shadow-sm"
          >
            Why Book With Faremaps
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-none mb-4 uppercase">
            Elite Travel Benefits
          </h2>
          <p className="text-slate-500 text-xs md:text-sm font-semibold leading-relaxed">
            World-class connections, bank-level security protocols, and intelligent alert tools
            built to keep your corporate and leisure travel seamless.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className={`group bg-white border border-slate-100 rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1 ${f.glow}`}
            >
              {/* Premium light-theme geometric icon container */}
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center border mb-5 group-hover:scale-105 transition-transform duration-300 ${f.bg}`}
              >
                {f.icon}
              </div>

              <h3 className="font-extrabold text-sm md:text-base text-slate-800 mb-2 leading-snug group-hover:text-accent transition-colors">
                {f.title}
              </h3>

              <p className="text-slate-500 text-xs leading-relaxed font-semibold">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
